/**
 * AI spend report client (Plan 4's ledger, read side).
 *
 *   GET /agents/admin/ai-spend/report?from&to&groupBy=
 *   GET /agents/admin/ai-spend/events?...
 *   GET /agents/admin/ai-spend/orphans
 *
 * SUPER-ADMIN ONLY. costUsd, attempts, providerId and modelId appear in these
 * responses and must never be surfaced to a tenant (spec §9.2). Nothing in
 * platform-app or the POS terminal may import this module.
 */

import { adminGet } from './transport';

export type PivotDimension = 'tenant' | 'service' | 'feature' | 'role' | 'model';

export const PIVOT_DIMENSIONS: readonly PivotDimension[] = [
  'tenant', 'service', 'feature', 'role', 'model',
] as const;

export const PIVOT_LABELS: Record<PivotDimension, string> = {
  tenant: 'By tenant',
  service: 'By service',
  feature: 'By feature',
  role: 'By role',
  model: 'By model',
};

export interface AiSpendReportRow {
  key: string;
  label: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** True provider cost. Company-confidential. */
  costUsd: number;
  /** Credit count charged to the tenant. NOT dollars. */
  creditsCharged: number;
  /** Every attempt the vendor billed, ≥ requests when the router retried. */
  attempts: number;
  failedCount: number;
  /** Portion of costUsd flagged costEstimated (aborted streams, spec M4). */
  estimatedCostUsd: number;
  avgLatencyMs: number;
}

export interface AiSpendEventRow {
  occurredAt: string;
  tenantId: string;
  tenantName?: string;
  service: string;
  feature: string;
  roleId: string;
  providerId: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  attempts: number;
  costUsd: number;
  costEstimated: boolean;
  creditsCharged: number;
  latencyMs: number;
  outcome: 'success' | 'failed' | 'aborted';
}

export interface AiSpendReport {
  range: { fromIso: string; toIso: string };
  dimension: PivotDimension;
  rows: AiSpendReportRow[];
  /** From the registry CONFIG row — needed to price creditsCharged. */
  creditUnitPriceUsd: number;
}

export interface AiSpendOrphans {
  count: number;
  oldestAgeMs: number;
  /** Reserved-but-unsettled spend. Evidence of cost we may never have recorded. */
  estimatedUnrecordedUsd: number;
}

export async function getAiSpendReport(
  dimension: PivotDimension,
  range: { fromIso?: string; toIso?: string },
): Promise<AiSpendReport> {
  const p = new URLSearchParams();
  p.set('groupBy', dimension);
  if (range.fromIso) p.set('from', range.fromIso);
  if (range.toIso) p.set('to', range.toIso);
  const body = await adminGet<Partial<AiSpendReport>>(`/agents/admin/ai-spend/report?${p.toString()}`);
  return {
    range: body.range ?? { fromIso: range.fromIso ?? '', toIso: range.toIso ?? '' },
    dimension,
    rows: body.rows ?? [],
    creditUnitPriceUsd: body.creditUnitPriceUsd ?? 0.01,
  };
}

export async function getAiSpendEvents(filters: {
  tenantId?: string;
  roleId?: string;
  modelId?: string;
  fromIso?: string;
  toIso?: string;
  limit?: number;
  exclusiveStartKey?: string;
}): Promise<{ items: AiSpendEventRow[]; lastEvaluatedKey?: string | null }> {
  const p = new URLSearchParams();
  if (filters.tenantId) p.set('tenantId', filters.tenantId);
  if (filters.roleId) p.set('roleId', filters.roleId);
  if (filters.modelId) p.set('modelId', filters.modelId);
  if (filters.fromIso) p.set('from', filters.fromIso);
  if (filters.toIso) p.set('to', filters.toIso);
  p.set('limit', String(filters.limit ?? 100));
  if (filters.exclusiveStartKey) p.set('exclusiveStartKey', filters.exclusiveStartKey);
  const body = await adminGet<{ items?: AiSpendEventRow[]; lastEvaluatedKey?: string | null }>(
    `/agents/admin/ai-spend/events?${p.toString()}`,
  );
  return { items: body.items ?? [], lastEvaluatedKey: body.lastEvaluatedKey ?? null };
}

export async function getAiSpendOrphans(): Promise<AiSpendOrphans> {
  const body = await adminGet<Partial<AiSpendOrphans>>('/agents/admin/ai-spend/orphans');
  return {
    count: body.count ?? 0,
    oldestAgeMs: body.oldestAgeMs ?? 0,
    estimatedUnrecordedUsd: body.estimatedUnrecordedUsd ?? 0,
  };
}
